import { OrganisationFeatureFlags } from "shared";
import { useMemo } from "react";

import { Select } from "../../../../../../common/Atoms/Select";
import { useGetSelectedOrganisation } from "../../../../../hooks/useGetSelectedOrganisation";
import { useGetStatementBuilderTemplatesQuery } from "../../../../../services/api/statementBuilderApi/statementBuilder";

const DEFAULT_VALUE = `default`;
const STANDARD_VALUE = `standard`;

interface StatementTemplateChoice {
  statement_template_id?: number | null;
  use_standard_statement?: boolean;
}

interface StatementTemplateSelectorProps {
  policy: StatementTemplateChoice;
  handleChange: (e: { name: string; value: string }) => void;
}

/** The selector emits one `statement_template_id` change; form handlers map it onto both stored fields */
export function statementTemplateChoiceFields(value: unknown): Required<StatementTemplateChoice> {
  const standard = value === STANDARD_VALUE;

  return {
    statement_template_id: value === DEFAULT_VALUE || standard ? null : Number(value),
    use_standard_statement: standard,
  };
}

export function StatementTemplateSelector({ policy, handleChange }: StatementTemplateSelectorProps) {
  const currentOrg = useGetSelectedOrganisation();
  const enabled = !!currentOrg?.featureFlags?.includes(OrganisationFeatureFlags.AI_STATEMENT_BUILDER);
  const { data, isLoading } = useGetStatementBuilderTemplatesQuery(
    { organisationId: currentOrg?.id || `` },
    { skip: !currentOrg?.id || !enabled },
  );

  const options = useMemo(() => {
    const published = (data?.templates || []).filter(t => t.hasPublished);
    const list = [
      { label: `Organisation default`, value: DEFAULT_VALUE },
      { label: `Standard template`, value: STANDARD_VALUE },
      ...published.map(t => ({ label: t.enabled ? `${t.title} (default)` : t.title, value: String(t.id) })),
    ];

    // Policy points at a template that is not published (or was deleted)
    if (!policy.use_standard_statement && policy.statement_template_id && !published.some(t => t.id === policy.statement_template_id)) {
      list.push({ label: `Unknown template`, value: String(policy.statement_template_id) });
    }

    return list;
  }, [data, policy.statement_template_id, policy.use_standard_statement]);

  if (!enabled) {
    return null;
  }

  const selected = policy.use_standard_statement
    ? STANDARD_VALUE
    : policy.statement_template_id
      ? String(policy.statement_template_id)
      : DEFAULT_VALUE;

  function onChange({ value }: { value: string | number | boolean }) {
    handleChange({ name: `statement_template_id`, value: String(value) });
  }

  return (
    <Select
      name={ `statement_template_id` }
      label={ `Statement PDF template` }
      helpIcon={ `Which AI statement template the attached outstanding statement uses.` }
      options={ options }
      selected={ selected }
      onChange={ onChange }
      loading={ isLoading }
    />
  );
}
